/**
 * Armement (capacites + limitations) du personnage.
 *
 * COF2 distingue les **maitrises** d'un personnage : un PJ peut toujours
 * UTILISER une arme/armure non maitrisee mais subit alors :
 * - un de malus en attaque (armes non maitrisees)
 * - perd toutes les capacites de profil restreintes par armure (armures non
 *   maitrisees)
 * - regle specifique armes a poudre : 1-2 au d20 = explosion (1d4 degree DM
 *   + arme inutilisable pour le combat)
 *
 * `armement_capacites` et `armement_limitations` sont DERIVES du personnage
 * (calcules a la volee par `computeArmement` a partir de profil + peuple +
 * voies + equipement). Pas stockes dans `PersonnageSchema`.
 *
 * Source-archive : voir docs/audit-armement.md (Phase O.1).
 */

/** Categories d'armes officielles (12) plus types DM (contondant/tranchant/percant). */
export interface ArmementCapacites {
  /** Maitrise par categorie d'arme. true = utilisable sans de malus. */
  readonly armes: {
    readonly contact_une_main: boolean;
    readonly contact_deux_mains: boolean;
    readonly distance: boolean;
    readonly poudre: boolean;
    /** Maitrise par type de DM (pretre = contondant only). */
    readonly contondant: boolean;
    readonly tranchant: boolean;
    readonly percant: boolean;
  };

  /**
   * Liste explicite d'armes specifiques maitrisees (cas mages : dague, baton ;
   * cas druide : dague, baton noueux, epieu, javelot, arc court).
   * Si `armes_specifiques` est non-vide ET les booleens `armes.*` sont tous
   * false, alors le profil maitrise UNIQUEMENT cette liste fermee.
   */
  readonly armes_specifiques: readonly string[];

  /** Armures maitrisees. mailles_legere = chemise (DEF+4) ; mailles_lourde = cotte (DEF+5). */
  readonly armures: {
    readonly aucune: true;
    readonly cuir: boolean;
    readonly cuir_renforce: boolean;
    readonly mailles_legere: boolean;
    readonly mailles_lourde: boolean;
    readonly plaques: boolean;
  };

  /** Boucliers maitrises (par taille). */
  readonly boucliers: {
    readonly petit: boolean;
    readonly normal: boolean;
    readonly grand: boolean;
  };

  /**
   * Arme sacree pour pretre, derivee de la religion choisie.
   * Si presente, AJOUTE cette arme aux maitrises (exception au "contondant only").
   */
  readonly arme_sacree?: string | undefined;
}

/** Limitations d'armement (DM max, armes specifiquement interdites). */
export interface ArmementLimitations {
  /** DM max global (toutes armes). Cas Halfelin : >1d10 interdit. */
  readonly dm_max_total?: number;
  /** DM max a une main. Cas Halfelin : 1d6 max a 1 main (>1d6 force 2 mains). */
  readonly dm_max_une_main?: number;
  /** Armes specifiquement interdites (par id ou categorie). Cas Halfelin : arc-long, arbalete-lourde. */
  readonly armes_interdites?: readonly string[];
}

/** Resultat du calcul derive. */
export interface ArmementDerive {
  readonly capacites: ArmementCapacites;
  readonly limitations: ArmementLimitations;
}

/** Modification d'armement appliquee par un rang de voie. */
export interface ArmementModificationsRang {
  readonly ajoute_armes_specifiques?: readonly string[];
  readonly ajoute_categories_armes?: {
    readonly contact_une_main?: boolean;
    readonly contact_deux_mains?: boolean;
    readonly distance?: boolean;
    readonly poudre?: boolean;
    readonly contondant?: boolean;
    readonly tranchant?: boolean;
    readonly percant?: boolean;
  };
  readonly ajoute_armures?: {
    readonly cuir?: boolean;
    readonly cuir_renforce?: boolean;
    readonly mailles_legere?: boolean;
    readonly mailles_lourde?: boolean;
    readonly plaques?: boolean;
  };
  readonly ajoute_boucliers?: {
    readonly petit?: boolean;
    readonly normal?: boolean;
    readonly grand?: boolean;
  };
  readonly limitations?: ArmementLimitations;
}

/**
 * Calcule l'armement derive d'un personnage en fusionnant :
 * - les capacites/limitations du profil principal
 * - les modifications appliquees par les voies acquises (rangs >= 1)
 *
 * Regles de fusion :
 * - `armes` (booleens) : OR (capacite acquise non perdue)
 * - `armes_specifiques` : union (unique)
 * - `armures` (booleens) : OR
 * - `boucliers` (booleens) : OR
 * - `arme_sacree` : conservee si presente
 * - `limitations` : pour dm_max_*, le plus restrictif gagne ; pour
 *   armes_interdites, union
 */
export function computeArmement(
  profilArmement: ArmementDerive,
  modifications: readonly ArmementModificationsRang[],
): ArmementDerive {
  const armes = { ...profilArmement.capacites.armes };
  const armures = { ...profilArmement.capacites.armures };
  const boucliers = { ...profilArmement.capacites.boucliers };
  const armesSpecifiquesSet = new Set(profilArmement.capacites.armes_specifiques);
  const limitations: { -readonly [K in keyof ArmementLimitations]: ArmementLimitations[K] } = {
    ...profilArmement.limitations,
  };

  for (const mod of modifications) {
    if (mod.ajoute_categories_armes) {
      for (const k of Object.keys(mod.ajoute_categories_armes) as Array<
        keyof typeof mod.ajoute_categories_armes
      >) {
        if (mod.ajoute_categories_armes[k] === true) armes[k] = true;
      }
    }
    if (mod.ajoute_armes_specifiques && mod.ajoute_armes_specifiques.length > 0) {
      for (const a of mod.ajoute_armes_specifiques) armesSpecifiquesSet.add(a);
    }
    if (mod.ajoute_armures) {
      for (const k of Object.keys(mod.ajoute_armures) as Array<keyof typeof mod.ajoute_armures>) {
        if (mod.ajoute_armures[k] === true) armures[k] = true;
      }
    }
    if (mod.ajoute_boucliers) {
      for (const k of Object.keys(mod.ajoute_boucliers) as Array<
        keyof typeof mod.ajoute_boucliers
      >) {
        if (mod.ajoute_boucliers[k] === true) boucliers[k] = true;
      }
    }
    if (mod.limitations) {
      if (mod.limitations.dm_max_total !== undefined) {
        const cur = limitations.dm_max_total ?? Number.POSITIVE_INFINITY;
        if (mod.limitations.dm_max_total < cur) {
          limitations.dm_max_total = mod.limitations.dm_max_total;
        }
      }
      if (mod.limitations.dm_max_une_main !== undefined) {
        const cur = limitations.dm_max_une_main ?? Number.POSITIVE_INFINITY;
        if (mod.limitations.dm_max_une_main < cur) {
          limitations.dm_max_une_main = mod.limitations.dm_max_une_main;
        }
      }
      if (mod.limitations.armes_interdites) {
        const set = new Set(limitations.armes_interdites ?? []);
        for (const a of mod.limitations.armes_interdites) set.add(a);
        limitations.armes_interdites = [...set];
      }
    }
  }

  return {
    capacites: {
      armes,
      armes_specifiques: [...armesSpecifiquesSet],
      armures,
      boucliers,
      arme_sacree: profilArmement.capacites.arme_sacree,
    },
    limitations,
  };
}

/**
 * Genere une liste descriptive des armes maitrisees pour affichage UI.
 * Format compatible avec l'ancien `armes_autorisees: string[]`.
 */
export function describeArmesMaitrisees(c: ArmementCapacites): string[] {
  const parts: string[] = [];
  if (c.armes.contact_une_main && c.armes.contact_deux_mains) {
    parts.push('toutes armes de contact');
  } else if (c.armes.contact_une_main) {
    parts.push('armes de contact a une main');
  } else if (c.armes.contact_deux_mains) {
    parts.push('armes de contact a deux mains');
  }
  if (c.armes.distance) parts.push('toutes armes a distance');
  if (c.armes.poudre) parts.push('armes a poudre');
  if (!c.armes.contact_une_main && !c.armes.contact_deux_mains && !c.armes.distance) {
    // Profil avec liste fermee uniquement (mages, druide)
    if (c.armes.contondant && !c.armes.tranchant && !c.armes.percant) {
      parts.push('armes contondantes');
    }
  }
  if (c.armes_specifiques.length > 0) {
    parts.push(...c.armes_specifiques);
  }
  if (c.arme_sacree) parts.push(`arme sacree (${c.arme_sacree})`);
  return parts.length > 0 ? parts : ['aucune'];
}

/**
 * Genere une liste descriptive des armures maitrisees pour affichage UI.
 */
export function describeArmuresMaitrisees(c: ArmementCapacites): string[] {
  const parts: string[] = [];
  if (c.armures.plaques) parts.push("jusqu'a plaques");
  else if (c.armures.mailles_lourde) parts.push("jusqu'a cotte de mailles");
  else if (c.armures.mailles_legere) parts.push("jusqu'a chemise de mailles");
  else if (c.armures.cuir_renforce) parts.push("jusqu'au cuir renforce");
  else if (c.armures.cuir) parts.push('cuir simple');
  else parts.push('aucune armure');
  if (c.boucliers.grand) parts.push('boucliers (tous)');
  else if (c.boucliers.normal) parts.push('petit + normal');
  else if (c.boucliers.petit) parts.push('petit bouclier');
  return parts;
}
